import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { launchOfferAPI } from '../lib/api';
import { Dog, House, MapPin, Heart, Star, Shield, ArrowRight, PawPrint, Gift, Lightning, CheckCircle, Users, Trophy, Clock, WhatsappLogo, Quotes, Smiley } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SERVICES = [
  { icon: Dog, title: 'Passeio Diário', description: 'Passeios regulares para manter o seu cão feliz, saudável e bem socializado', image: 'https://images.unsplash.com/photo-1663840196762-04dba3d356e1?w=400&h=300&fit=crop', price: 'A partir de 10€' },
  { icon: House, title: 'Hospedagem', description: 'Cuidado 24h em casa de um pet sitter de confiança, como se fosse a sua própria casa', image: 'https://images.unsplash.com/photo-1763942467199-af4207ee5320?w=400&h=300&fit=crop', price: 'A partir de 25€/noite' },
  { icon: MapPin, title: 'Visita ao Domicílio', description: 'Visitas à sua casa para alimentar, brincar e dar atenção ao seu pet', image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop', price: 'A partir de 12€' },
  { icon: Heart, title: 'Dog Sitting', description: 'Cuidados completos e personalizados durante as suas viagens ou ausências', image: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400&h=300&fit=crop', price: 'A partir de 20€/dia' },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Pesquise na sua zona', description: 'Utilize o nosso mapa interativo para encontrar pet sitters verificados perto de si em todo o Portugal.', icon: MapPin },
  { step: '02', title: 'Escolha o prestador ideal', description: 'Leia avaliações reais, veja fotos e compare serviços. Fale diretamente com o prestador antes de decidir.', icon: Star },
  { step: '03', title: 'Reserve com segurança', description: 'Confirme a reserva de forma segura e acompanhe tudo em tempo real através da plataforma.', icon: Shield },
];

const PROVIDER_BENEFITS = ['Perfil profissional gratuito', 'Acesso a milhares de clientes em Portugal', 'Sistema de pagamentos integrado', 'Suporte e formação incluídos', 'Avaliações que constroem a sua reputação', 'Gestão fácil de pedidos e agenda'];

const DEFAULT_CONTENT = {
  hero_title: 'Encontre o Pet Sitter Perfeito',
  hero_subtitle: 'Conectamos donos de animais com pet sitters e passeadores de cães verificados em todo o Portugal.',
  hero_badge: 'O melhor cuidado para o seu melhor amigo 🐾',
  hero_cta_primary: 'Começar Agora — É Grátis!',
  hero_cta_secondary: 'Ver Prestadores',
  hero_social_proof: '+2.000 donos já confiam em nós',
  stat_providers: '500+', stat_providers_label: 'Prestadores Verificados',
  stat_animals: '2.000+', stat_animals_label: 'Animais Felizes',
  stat_rating: '4.9★', stat_rating_label: 'Avaliação Média',
  stat_coverage: '100%', stat_coverage_label: 'Portugal',
  whatsapp_number: '351928314198',
  whatsapp_message: 'Olá! Gostaria de saber mais sobre o PasseioPerfeito 🐾',
  whatsapp_section_title: 'Tem dúvidas? Fale connosco agora!',
  whatsapp_section_subtitle: 'A nossa equipa está disponível para o ajudar a encontrar o pet sitter ideal para o seu animal.',
  cta_section_title: 'Pronto para começar?',
  cta_section_subtitle: 'Junte-se a milhares de donos de animais que já encontraram o pet sitter perfeito em Portugal.',
  provider_section_title: 'Transforme o amor pelos animais num negócio',
  provider_section_subtitle: 'Junte-se à maior rede de pet sitters de Portugal. Defina os seus preços, gerencie a sua agenda e cresça o seu negócio.',
  testimonials: [
    { id: 't1', name: 'Ana Ribeiro', location: 'Porto', text: 'Encontrei a pet sitter perfeita para o meu Buddy. Profissional, carinhosa e muito responsável. Recomendo a toda a gente!', rating: 5, avatar: 'AR', pet: 'Dona do Buddy 🐕' },
    { id: 't2', name: 'Carlos Mendes', location: 'Lisboa', text: 'Serviço incrível! O meu gato ficou aos cuidados da Margarida enquanto estava em viagem. Fotos diárias e muito profissionalismo.', rating: 5, avatar: 'CM', pet: 'Dono do Mimi 🐱' },
    { id: 't3', name: 'Sofia Ferreira', location: 'Braga', text: 'A melhor plataforma de pet sitters em Portugal. Fácil de usar, prestadores verificados e preços acessíveis. 5 estrelas!', rating: 5, avatar: 'SF', pet: 'Dona da Luna 🐶' },
  ],
};

const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function LandingPage() {
  const { isAuthenticated, user } = useAuth();
  const [launchOffer, setLaunchOffer] = useState(null);
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    launchOfferAPI.getStatus().then(setLaunchOffer).catch(() => {});
    fetch(`${API_URL}/landing-content`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && Object.keys(data).length > 0) setContent(prev => ({ ...prev, ...data })); })
      .catch(() => {});
  }, []);

  const getDashboardLink = () => {
    if (!isAuthenticated) return '/login';
    if (!user?.role) return '/onboarding';
    return user.role === 'prestador' ? '/provider/dashboard' : '/client/home';
  };

  const whatsappUrl = `https://wa.me/${content.whatsapp_number}?text=${encodeURIComponent(content.whatsapp_message)}`;
  const stats = [
    { value: content.stat_providers, label: content.stat_providers_label, icon: Shield },
    { value: content.stat_animals, label: content.stat_animals_label, icon: PawPrint },
    { value: content.stat_rating, label: content.stat_rating_label, icon: Star },
    { value: content.stat_coverage, label: content.stat_coverage_label, icon: MapPin },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* WhatsApp Floating Button */}
      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
        <WhatsappLogo weight="fill" className="w-6 h-6" />
        <span className="font-semibold text-sm hidden sm:block">Fale Connosco</span>
      </a>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/20d15b39-0118-425d-8afb-26f0a4e64530/images/9203e212a96c816c0804427a28d695fb2112d055c5bb16a9b41d271409067e54.png')` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div className="max-w-2xl text-white" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm text-sm mb-6 border border-white/20">
              <PawPrint weight="fill" className="w-4 h-4 text-yellow-300" />{content.hero_badge}
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              {content.hero_title.split('Perfeito').length > 1 ? (
                <>{content.hero_title.split('Perfeito')[0]}<span className="text-yellow-300">Perfeito</span>{content.hero_title.split('Perfeito')[1]}</>
              ) : content.hero_title}
            </h1>
            <p className="text-xl text-white/85 mb-4 max-w-lg">{content.hero_subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to={getDashboardLink()}>
                <Button size="lg" className="rounded-full text-lg px-8 h-14 bg-yellow-400 hover:bg-yellow-300 text-black font-bold shadow-lg" data-testid="hero-cta">
                  {content.hero_cta_primary}<ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/client/providers">
                <Button size="lg" variant="outline" className="rounded-full text-lg px-8 h-14 bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="hero-secondary">
                  {content.hero_cta_secondary}
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4 mt-8">
              <div className="flex -space-x-2">
                {['bg-orange-400','bg-blue-400','bg-green-400','bg-purple-400'].map((c, i) => (
                  <div key={i} className={`w-8 h-8 rounded-full ${c} border-2 border-white flex items-center justify-center text-white text-xs font-bold`}>{['A','M','J','P'][i]}</div>
                ))}
              </div>
              <p className="text-white/80 text-sm"><strong className="text-white">{content.hero_social_proof}</strong></p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Launch Offer */}
      {launchOffer?.is_active && (
        <section className="relative overflow-hidden" data-testid="launch-offer-banner">
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 py-4 sm:py-5">
            <div className="container mx-auto px-4">
              <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-white" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-2"><Gift weight="fill" className="w-6 h-6 animate-bounce" /><span className="font-bold text-base sm:text-lg">Oferta de Lançamento!</span></div>
                <p className="text-sm sm:text-base text-center"><strong>Grátis para os primeiros {launchOffer.max_free_providers} prestadores</strong> — Restam apenas <span className="font-bold text-xl mx-1 bg-white/20 px-2 py-0.5 rounded">{launchOffer.remaining_spots}</span> vagas!</p>
                <Link to={isAuthenticated ? '/onboarding' : '/login'}><Button size="sm" className="rounded-full bg-white text-orange-600 hover:bg-white/90 font-bold" data-testid="launch-offer-cta"><Lightning weight="fill" className="w-4 h-4 mr-1" />Registar Grátis</Button></Link>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {stats.map((stat) => { const Icon = stat.icon; return (
              <motion.div key={stat.label} variants={itemVariants} className="text-center">
                <Icon weight="fill" className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <div className="text-3xl sm:text-4xl font-bold mb-1">{stat.value}</div>
                <div className="text-primary-foreground/70 text-sm">{stat.label}</div>
              </motion.div>
            ); })}
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Serviços</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-4">Tudo o que o seu pet precisa</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Desde passeios diários a hospedagem completa, encontre o serviço ideal para cada situação</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {SERVICES.map((service) => { const Icon = service.icon; return (
              <motion.div key={service.title} variants={itemVariants}>
                <Card className="h-full overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4"><div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow"><Icon weight="fill" className="w-6 h-6 text-primary" /></div></div>
                    <div className="absolute top-3 right-3 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">{service.price}</div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{service.description}</p>
                    <Link to="/client/providers"><Button variant="outline" size="sm" className="w-full rounded-full">Ver Prestadores <ArrowRight className="ml-1 w-4 h-4" /></Button></Link>
                  </CardContent>
                </Card>
              </motion.div>
            ); })}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Como Funciona</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-4">Simples, rápido e seguro</h2>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {HOW_IT_WORKS.map((step) => { const Icon = step.icon; return (
              <motion.div key={step.step} variants={itemVariants} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4 shadow-lg"><Icon weight="duotone" className="w-8 h-8 text-primary-foreground" /></div>
                <span className="text-5xl font-black text-primary/10 block -mt-2 mb-2">{step.step}</span>
                <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm max-w-xs mx-auto">{step.description}</p>
              </motion.div>
            ); })}
          </motion.div>
          <motion.div className="text-center mt-12" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <Link to={getDashboardLink()}><Button size="lg" className="rounded-full px-10">Encontrar Pet Sitter Agora <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      {content.testimonials?.length > 0 && (
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="text-sm font-medium text-primary uppercase tracking-wider">Testemunhos</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-4">O que dizem os nossos clientes</h2>
            </motion.div>
            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {content.testimonials.map((t) => (
                <motion.div key={t.id} variants={itemVariants}>
                  <Card className="h-full p-6 hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      <Quotes weight="fill" className="w-8 h-8 text-primary/30 mb-3" />
                      <div className="flex mb-3">{[...Array(t.rating||5)].map((_,i)=><Star key={i} weight="fill" className="w-4 h-4 text-yellow-400"/>)}</div>
                      <p className="text-muted-foreground text-sm mb-5 italic">"{t.text}"</p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">{t.avatar}</div>
                        <div><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-muted-foreground">{t.pet} · {t.location}</p></div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Provider Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-sm font-medium text-primary uppercase tracking-wider">Para Prestadores</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-4">{content.provider_section_title}</h2>
              <p className="text-muted-foreground mb-6">{content.provider_section_subtitle}</p>
              <ul className="space-y-3 mb-8">{PROVIDER_BENEFITS.map(b=><li key={b} className="flex items-center gap-3"><CheckCircle weight="fill" className="w-5 h-5 text-green-500 flex-shrink-0"/><span className="text-sm">{b}</span></li>)}</ul>
              <Link to={isAuthenticated?'/onboarding':'/login'}><Button size="lg" className="rounded-full px-8">Registar como Prestador <ArrowRight className="ml-2 w-5 h-5"/></Button></Link>
            </motion.div>
            <motion.div className="grid grid-cols-2 gap-4" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              {[{icon:Trophy,label:'Ganhos médios',value:'800€/mês',color:'bg-yellow-50 text-yellow-600'},{icon:Users,label:'Clientes ativos',value:'2.000+',color:'bg-blue-50 text-blue-600'},{icon:Clock,label:'Horário flexível',value:'100%',color:'bg-green-50 text-green-600'},{icon:Smiley,label:'Satisfação',value:'4.9/5',color:'bg-purple-50 text-purple-600'}].map(item=>{const Icon=item.icon;return(
                <Card key={item.label} className="p-5 text-center hover:shadow-md transition-shadow">
                  <div className={`w-12 h-12 rounded-full ${item.color} flex items-center justify-center mx-auto mb-3`}><Icon weight="fill" className="w-6 h-6"/></div>
                  <div className="text-2xl font-bold mb-1">{item.value}</div>
                  <div className="text-xs text-muted-foreground">{item.label}</div>
                </Card>
              );})}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WhatsApp Section */}
      <section className="py-16 bg-green-50 dark:bg-green-950/20">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <WhatsappLogo weight="fill" className="w-12 h-12 text-green-500 mx-auto mb-4"/>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">{content.whatsapp_section_title}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{content.whatsapp_section_subtitle}</p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="rounded-full bg-green-500 hover:bg-green-600 text-white px-10 h-14 text-lg font-bold shadow-lg">
                <WhatsappLogo weight="fill" className="w-6 h-6 mr-2"/>Falar no WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <PawPrint weight="fill" className="w-12 h-12 mx-auto mb-4 opacity-80"/>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{content.cta_section_title}</h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">{content.cta_section_subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={getDashboardLink()}><Button size="lg" variant="secondary" className="rounded-full text-lg px-8 h-14 font-bold" data-testid="cta-client">Sou Dono de Animal 🐾</Button></Link>
              <Link to={isAuthenticated?'/onboarding':'/login'}><Button size="lg" variant="outline" className="rounded-full text-lg px-8 h-14 border-white/30 text-white hover:bg-white/10" data-testid="cta-provider">Quero Ser Prestador 💼</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
